import React, { Suspense, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { useViewAs } from '../context/ViewAsContext'
import { UtFlagProvider, useUt } from '../context/UtFlagContext'
import Header from './layout/Header'
import Footer from './layout/Footer'
import ViewAsIndicator from './admin/ViewAsIndicator'
import { UtShell } from './ut/shell/UtShell'
import { RouteLoader } from './ui/RouteLoader'
import { subscribeToTeamAnnouncements, showTeamAnnouncementNotification } from '../utils/teamNotifications'

interface LayoutProps {
  children: React.ReactNode
}

const LayoutInner: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { isViewingAs } = useViewAs();
  const { enabled: utEnabled, loading: utLoading } = useUt();

  useEffect(() => {
    if (user?.id) {
      const unsubscribe = subscribeToTeamAnnouncements(
        user.id,
        showTeamAnnouncementNotification
      );

      return () => {
        unsubscribe();
      };
    }
  }, [user?.id]);

  // Hold the page until the ut-redesign flag resolves so neither UI flashes.
  // This only happens on a full page load; Layout stays mounted between routes.
  if (utLoading) return null;

  if (utEnabled) {
    return (
      <>
        <ViewAsIndicator />
        {isViewingAs && <div className="h-16" />}
        <UtShell>{children}</UtShell>
      </>
    );
  }

  return (
    <>
      <ViewAsIndicator />
      {isViewingAs && <div className="h-16" />}
      <div className="flex flex-col min-h-screen">
        <Header />
        <AnimatePresence mode="wait">
          <motion.main
            className="flex-grow container mx-auto px-4 py-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/*
              Suspense lives here, below the nav, so fetching a lazy route chunk only
              swaps the page content. Keeping it above <Layout> used to hide the whole
              shell mid-navigation, which stranded open nav overlays on screen.
            */}
            <Suspense fallback={<RouteLoader />}>{children}</Suspense>
          </motion.main>
        </AnimatePresence>
        <Footer />
      </div>
    </>
  )
}

/** The ut-redesign flag is read once here; pages read it with useUt(). */
const Layout: React.FC<LayoutProps> = ({ children }) => (
  <UtFlagProvider>
    <LayoutInner>{children}</LayoutInner>
  </UtFlagProvider>
)

export default Layout
